(() => {
  if (window.Shopify?.designMode || customElements.get('yrt-image-viewer')) return;
  const clamp = (n, min, max) => Math.max(min, Math.min(n, max));
  class TutorialImageViewer extends HTMLElement {
    connectedCallback() {
      this.innerHTML = `<dialog class="yrt-viewer" aria-label="Crochet pattern image viewer">
        <button type="button" class="yrt-viewer-close" aria-label="Close image viewer">×</button>
        <div class="yrt-viewer-toolbar"><button type="button" data-action="out" aria-label="Zoom out">−</button><button type="button" data-action="reset" aria-label="Reset image zoom">100%</button><button type="button" data-action="in" aria-label="Zoom in">+</button></div>
        <div class="yrt-viewer-stage"><img class="yrt-viewer-image" alt="" draggable="false"><span class="yrt-viewer-message" role="status"></span></div>
        <div class="yrt-viewer-footer"><p class="yrt-viewer-caption"></p><div class="yrt-viewer-nav"><button type="button" data-action="prev" aria-label="Previous image">←</button><span class="yrt-viewer-count" aria-live="polite"></span><button type="button" data-action="next" aria-label="Next image">→</button></div><a class="yrt-viewer-full" target="_blank" rel="noopener noreferrer">View Full Size ↗</a></div>
      </dialog>`;
      this.dialog = this.querySelector('dialog');
      this.stage = this.querySelector('.yrt-viewer-stage');
      this.img = this.querySelector('img');
      this.message = this.querySelector('.yrt-viewer-message');
      this.caption = this.querySelector('.yrt-viewer-caption');
      this.closeButton = this.querySelector('.yrt-viewer-close');
      this.points = new Map();
      this.closeButton.addEventListener('click', () => this.dialog.close());
      this.dialog.addEventListener('close', () => this.restore());
      this.dialog.addEventListener('click', e => {
        if (e.target === this.dialog) this.dialog.close();
        const action = e.target.closest('[data-action]')?.dataset.action;
        if (action === 'prev') this.select(this.index - 1);
        if (action === 'next') this.select(this.index + 1);
        if (action === 'reset') this.zoom(1);
        if (action === 'in') this.zoom(this.scale + .5);
        if (action === 'out') this.zoom(this.scale - .5);
      });
      this.dialog.addEventListener('keydown', e => {
        if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
          e.preventDefault(); this.select(this.index + (e.key === 'ArrowRight' ? 1 : -1));
        }
        if (e.key === 'Tab') {
          const targets = [...this.dialog.querySelectorAll('button:not(:disabled),a[href]')].filter(x => x.getClientRects().length);
          const first = targets[0], last = targets.at(-1);
          if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
          else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
        }
      });
      this.img.addEventListener('load', () => { this.message.textContent = ''; this.fit(); });
      this.img.addEventListener('error', () => { this.message.textContent = 'Image could not load. Please close and try again.'; });
      this.stage.addEventListener('pointerdown', e => this.down(e));
      this.stage.addEventListener('pointermove', e => this.move(e));
      this.stage.addEventListener('pointerup', e => this.up(e));
      this.stage.addEventListener('pointercancel', e => { this.points.delete(e.pointerId); this.gesture = null; this.dialog.dataset.dragging = 'false'; });
      this.stage.addEventListener('dblclick', e => {
        if (!this.start?.image || performance.now() - (this.touchTime || 0) < 600) return;
        e.preventDefault(); this.toggle(e.clientX, e.clientY);
      });
      this.stage.addEventListener('wheel', e => {
        if (e.ctrlKey || e.metaKey) return;
        e.preventDefault(); this.zoom(this.scale + (e.deltaY < 0 ? .25 : -.25), e.clientX, e.clientY);
      }, { passive: false });
      this.observer = new ResizeObserver(() => { if (this.dialog.open) this.fit(); });
      this.observer.observe(this.stage);
    }
    open(trigger) {
      this.items = [...document.querySelectorAll('.yrt-photos .yrt-zoom-trigger')];
      this.origin = trigger;
      this.scrollY = window.scrollY;
      this.savedBody = {};
      for (const key of ['position','top','left','right','width','overflow']) this.savedBody[key] = document.body.style[key];
      Object.assign(document.body.style, {position:'fixed', top:`-${this.scrollY}px`, left:'0', right:'0', width:'100%', overflow:'hidden'});
      this.dialog.showModal();
      this.select(this.items.indexOf(trigger));
      this.closeButton.focus({preventScroll:true});
    }
    restore() {
      Object.assign(document.body.style, this.savedBody);
      window.scrollTo({top:this.scrollY,left:0,behavior:'instant'});
      this.origin?.focus({preventScroll:true});
      this.img.removeAttribute('src');
      this.points.clear();
      this.gesture = null;
    }
    select(index) {
      this.index = (index + this.items.length) % this.items.length;
      const item = this.items[this.index];
      this.scale = 1; this.x = 0; this.y = 0; this.lastTap = null;
      this.points.clear(); this.gesture = null;
      this.img.style.transform = 'none';
      this.img.alt = item.dataset.yrtAlt || '';
      this.caption.textContent = item.dataset.yrtCaption || '';
      this.caption.hidden = !this.caption.textContent.trim();
      this.querySelector('.yrt-viewer-nav').hidden = this.items.length < 2;
      this.querySelector('.yrt-viewer-count').textContent = `${this.index + 1} / ${this.items.length}`;
      this.querySelector('.yrt-viewer-full').href = item.dataset.yrtFull;
      this.message.textContent = 'Loading image…';
      this.img.src = item.dataset.yrtFull;
      if (this.img.complete && this.img.naturalWidth) { this.message.textContent = ''; this.fit(); }
      this.paint();
    }
    fit() {
      if (!this.img.naturalWidth) return;
      const ratio = Math.min(this.stage.clientWidth / this.img.naturalWidth, this.stage.clientHeight / this.img.naturalHeight, 1);
      this.baseWidth = this.img.naturalWidth * ratio;
      this.baseHeight = this.img.naturalHeight * ratio;
      this.img.style.width = `${this.baseWidth}px`;
      this.img.style.height = `${this.baseHeight}px`;
      this.paint();
    }
    paint() {
      const maxX = Math.max(0, ((this.baseWidth || 0) * this.scale - this.stage.clientWidth) / 2);
      const maxY = Math.max(0, ((this.baseHeight || 0) * this.scale - this.stage.clientHeight) / 2);
      this.x = clamp(this.x, -maxX, maxX); this.y = clamp(this.y, -maxY, maxY);
      this.img.style.transform = `translate(${this.x}px,${this.y}px) scale(${this.scale})`;
      this.dialog.dataset.zoomed = String(this.scale > 1);
      this.querySelector('[data-action="reset"]').textContent = `${Math.round(this.scale * 100)}%`;
      this.querySelector('[data-action="out"]').disabled = this.scale <= 1;
      this.querySelector('[data-action="in"]').disabled = this.scale >= 4;
    }
    zoom(value, clientX, clientY) {
      const next = clamp(value, 1, 4), factor = next / this.scale;
      const rect = this.stage.getBoundingClientRect();
      const cx = clientX == null ? 0 : clientX - rect.left - rect.width / 2;
      const cy = clientY == null ? 0 : clientY - rect.top - rect.height / 2;
      this.x = cx - (cx - this.x) * factor;
      this.y = cy - (cy - this.y) * factor;
      this.scale = next;
      if (next === 1) this.x = this.y = 0;
      this.paint();
    }
    toggle(x,y) { this.zoom(this.scale > 1 ? 1 : 2.5,x,y); }
    down(e) {
      if (e.button !== 0) return;
      this.stage.setPointerCapture(e.pointerId);
      this.points.set(e.pointerId, {x:e.clientX,y:e.clientY});
      this.start = {x:e.clientX,y:e.clientY,time:performance.now(),image:e.target === this.img};
      this.moved = false;
      this.hadPinch = this.points.size > 1;
      this.beginGesture();
    }
    beginGesture() {
      const p = [...this.points.values()];
      this.gesture = {x:this.x,y:this.y,scale:this.scale,point:p[0]};
      if (p.length > 1) {
        this.gesture.distance = Math.hypot(p[1].x-p[0].x,p[1].y-p[0].y);
        this.gesture.center = {x:(p[0].x+p[1].x)/2,y:(p[0].y+p[1].y)/2};
      }
    }
    move(e) {
      if (!this.points.has(e.pointerId) || !this.gesture) return;
      this.points.set(e.pointerId,{x:e.clientX,y:e.clientY});
      const p = [...this.points.values()], g = this.gesture;
      if (Math.hypot(e.clientX-this.start.x,e.clientY-this.start.y)>8) this.moved = true;
      if (p.length > 1 && g.distance) {
        this.hadPinch = true;
        const next = clamp(g.scale*Math.hypot(p[1].x-p[0].x,p[1].y-p[0].y)/g.distance,1,4);
        const rect = this.stage.getBoundingClientRect();
        const cx = (p[0].x+p[1].x)/2-rect.left-rect.width/2;
        const cy = (p[0].y+p[1].y)/2-rect.top-rect.height/2;
        this.x = cx-(g.center.x-rect.left-rect.width/2-g.x)*next/g.scale;
        this.y = cy-(g.center.y-rect.top-rect.height/2-g.y)*next/g.scale;
        this.scale = next;
      } else if (this.scale > 1) {
        this.x = g.x+e.clientX-g.point.x; this.y = g.y+e.clientY-g.point.y;
      }
      this.dialog.dataset.dragging = String(this.moved);
      this.paint();
    }
    up(e) {
      if (!this.points.has(e.pointerId)) return;
      this.points.delete(e.pointerId);
      this.dialog.dataset.dragging = 'false';
      if (this.points.size) { this.beginGesture(); return; }
      this.gesture = null;
      if (this.hadPinch) { this.lastTap = null; return; }
      const dx=e.clientX-this.start.x, dy=e.clientY-this.start.y;
      if (this.scale === 1 && Math.abs(dx)>60 && Math.abs(dx)>Math.abs(dy)*1.5 && this.items.length>1) {
        this.select(this.index+(dx<0?1:-1)); return;
      }
      if (this.moved) return;
      if (!this.start.image) { this.dialog.close(); return; }
      if (e.pointerType === 'touch') {
        const now=performance.now(); this.touchTime=now;
        if (this.lastTap && now-this.lastTap.time<320 && Math.hypot(e.clientX-this.lastTap.x,e.clientY-this.lastTap.y)<35) {
          this.toggle(e.clientX,e.clientY); this.lastTap=null;
        } else this.lastTap={time:now,x:e.clientX,y:e.clientY};
      }
    }
  }
  customElements.define('yrt-image-viewer', TutorialImageViewer);
  document.addEventListener('click', e => {
    const trigger = e.target.closest('.yrt-photos .yrt-zoom-trigger');
    if (!trigger || window.Shopify?.designMode) return;
    let viewer = document.querySelector('yrt-image-viewer');
    if (!viewer) { viewer=document.createElement('yrt-image-viewer'); document.body.append(viewer); }
    if (!viewer.dialog.open) viewer.open(trigger);
  });
})();

